import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, ClipboardCheck, Info } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { apiFetch } from '../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * NewReconciliation — full-page version of "Start Bank Reconciliation".
 * Replaces the modal flow in ReconciliationList for deeper context and back-button support.
 */
export default function NewReconciliation() {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;

  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBank, setSelectedBank] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statementBalance, setStatementBalance] = useState('');
  const [lastCompletedDate, setLastCompletedDate] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!wsId) return;
    const load = async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/banking/accounts?workspace_id=${wsId}`);
        if (r.ok) {
          const data = await r.json();
          // Endpoint may return either a bare array or { accounts: [...] }
          const list = Array.isArray(data) ? data : (data.accounts || data.bank_accounts || []);
          setBankAccounts(list);
        }
      } catch { /* noop */ }
    };
    load();
  }, [wsId]);

  // Fetch last completed end date when a bank is picked so users can chain reconciliations.
  useEffect(() => {
    if (!selectedBank || !wsId) { setLastCompletedDate(null); return; }
    const load = async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/banking/reconciliations?workspace_id=${wsId}&bank_id=${selectedBank}&status=completed&limit=1`);
        if (r.ok) {
          const data = await r.json();
          const list = Array.isArray(data) ? data : (data.reconciliations || []);
          if (list?.[0]?.end_date) setLastCompletedDate(list[0].end_date);
        }
      } catch { /* noop */ }
    };
    load();
  }, [selectedBank, wsId]);

  const submit = async () => {
    const balance = parseFloat(statementBalance);
    if (isNaN(balance)) {
      toast.error('Please enter a valid statement balance');
      return;
    }
    setCreating(true);
    try {
      const r = await apiFetch(`${API_URL}/api/banking/reconciliations?workspace_id=${wsId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bank_id: selectedBank, start_date: startDate, end_date: endDate, statement_balance: balance }),
      });
      if (r.ok) {
        const data = await r.json();
        toast.success('Reconciliation started');
        navigate(`/banking/reconcile/${data.reconciliation_id}`);
      } else {
        const error = await r.json();
        if (error.detail?.error_code === 'reconciliation_in_progress' && error.detail.existing_reconciliation_id) {
          toast.error(error.detail.message);
          navigate(`/banking/reconcile/${error.detail.existing_reconciliation_id}`);
        } else {
          toast.error(error.detail?.message || error.detail || 'Failed to start reconciliation');
        }
      }
    } catch {
      toast.error('Failed to start reconciliation');
    } finally { setCreating(false); }
  };

  const canSubmit = selectedBank && startDate && endDate && statementBalance && !creating;

  return (
    <div className="container mx-auto p-6 max-w-2xl space-y-5" data-testid="new-reconciliation-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/banking/reconcile')} data-testid="back-btn">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to Reconciliations
      </Button>

      <div>
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-6 h-6 text-emerald-500" />
          <h1 className="text-2xl font-semibold tracking-tight">Start Bank Reconciliation</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Enter your bank statement details to begin reconciling against your books.
        </p>
      </div>

      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="space-y-2">
            <Label>Bank account</Label>
            <Select value={selectedBank} onValueChange={setSelectedBank}>
              <SelectTrigger data-testid="bank-select"><SelectValue placeholder="Select bank account" /></SelectTrigger>
              <SelectContent>
                {bankAccounts.length === 0 ? (
                  <div className="p-2 text-xs text-muted-foreground">No bank accounts connected yet.</div>
                ) : bankAccounts.map(b => (
                  <SelectItem key={b.bank_id} value={b.bank_id}>
                    {b.institution_name || b.account_name}{b.mask && ` ****${b.mask}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {lastCompletedDate && (
            <div className="p-3 rounded-lg bg-muted/40 text-xs flex gap-2 items-start" data-testid="last-completed-hint">
              <Info className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
              <p className="text-muted-foreground">
                Last completed reconciliation ended on{' '}
                <span className="font-medium text-foreground">{format(new Date(lastCompletedDate), 'MMM d, yyyy')}</span>.
                Pick a Start Date the day after to chain seamlessly.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Statement start date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="start-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Statement end date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="end-date-input" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Statement ending balance</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <Input type="number" step="0.01" className="pl-7" placeholder="0.00"
                value={statementBalance} onChange={(e) => setStatementBalance(e.target.value)}
                data-testid="statement-balance-input" />
            </div>
            <p className="text-xs text-muted-foreground">Enter the ending balance shown on your bank statement.</p>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between gap-2">
        <Button variant="outline" onClick={() => navigate('/banking/reconcile')} data-testid="cancel-btn">Cancel</Button>
        <Button onClick={submit} disabled={!canSubmit} data-testid="start-reconciliation-btn">
          {creating ? 'Starting…' : 'Start reconciliation'}
        </Button>
      </div>
    </div>
  );
}
